/** Kampanya tipleri ve yardımcılar — canlı veri: /campaigns.json (GoDaddy public_html'de düzenlenir) */

import { contactPathWithSubject } from './contact';

export type CampaignCta = {
  label: string;
  link: string;
};

export type Campaign = {
  id: string;
  slug: string;
  enabled: boolean;
  startDate?: string;
  endDate?: string;
  banner: {
    enabled: boolean;
    message: string;
    ctaLabel: string;
  };
  landing: {
    badge: string;
    title: string;
    subtitle: string;
    highlights: Array<{ icon: string; text: string }>;
    body: string[];
    ctaPrimary: CampaignCta;
    ctaSecondary?: CampaignCta;
    contactSubject: string;
  };
  meta: {
    title: string;
    description: string;
  };
};

/** JSON yüklenemezse kullanılan yedek (build / offline) */
export const FALLBACK_CAMPAIGNS: Campaign[] = [
  {
    id: 'sap-b1-demo-2025',
    slug: 'sap-b1-demo',
    enabled: true,
    endDate: '2026-12-31',
    banner: {
      enabled: true,
      message: 'SAP Business One demo kurulumu ve keşif görüşmesi için başvurun.',
      ctaLabel: 'Detaylar',
    },
    landing: {
      badge: 'SAP B1 Kampanyası',
      title: 'SAP Business One ile Tanışın',
      subtitle:
        'İşletmenize uygunluğu birlikte değerlendirelim — demo kurulum, süreç analizi ve danışmanlık görüşmesi.',
      highlights: [
        { icon: 'ri-building-2-line', text: 'SAP B1 sertifikalı danışmanlık' },
        { icon: 'ri-download-cloud-2-line', text: 'Demo kurulum paketi' },
        { icon: 'ri-calendar-check-line', text: 'İş saatleri içinde uzman görüşmesi' },
      ],
      body: [
        'Luminary olarak SAP Business One implementasyon, özelleştirme ve destek hizmetleri sunuyoruz. Boyum-IT ve Computec ekosistemiyle üretim, depo ve saha süreçlerinizi güçlendirebilirsiniz.',
        'Bu kampanya kapsamında ihtiyaçlarınızı dinleyip uygun yol haritasını paylaşıyoruz. Fiyat teklifi proje kapsamına göre ayrıca hazırlanır — web üzerinden bağlayıcı teklif verilmez.',
      ],
      ctaPrimary: { label: 'İletişim', link: contactPathWithSubject('SAP Business One') },
      ctaSecondary: { label: 'İndir', link: '/destek/indir' },
      contactSubject: 'SAP Business One',
    },
    meta: {
      title: 'SAP B1 Demo Kampanyası | Luminary Business Solutions',
      description:
        'SAP Business One demo kurulumu ve keşif görüşmesi. Luminary sertifikalı danışmanlık — İstanbul.',
    },
  },
];

export const CAMPAIGNS_JSON_URL = `${import.meta.env.BASE_URL}campaigns.json`;

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function isCampaignActive(campaign: Campaign): boolean {
  if (!campaign.enabled) return false;
  const today = todayIso();
  if (campaign.startDate && today < campaign.startDate) return false;
  if (campaign.endDate && today > campaign.endDate) return false;
  return true;
}

export function isValidCampaign(value: unknown): value is Campaign {
  if (!value || typeof value !== 'object') return false;
  const c = value as Campaign;
  return (
    typeof c.id === 'string' &&
    typeof c.slug === 'string' &&
    typeof c.enabled === 'boolean' &&
    c.banner != null &&
    typeof c.banner.message === 'string' &&
    c.landing != null &&
    typeof c.landing.title === 'string' &&
    c.meta != null &&
    typeof c.meta.title === 'string'
  );
}

export function parseCampaignsPayload(data: unknown): Campaign[] {
  if (!data || typeof data !== 'object') return [];
  const list = (data as { campaigns?: unknown }).campaigns;
  if (!Array.isArray(list)) return [];
  return list.filter(isValidCampaign);
}

export function getActiveBannerCampaign(campaigns: Campaign[]): Campaign | null {
  return campaigns.find((c) => isCampaignActive(c) && c.banner.enabled) ?? null;
}

export function getCampaignBySlug(campaigns: Campaign[], slug: string): Campaign | undefined {
  const campaign = campaigns.find((c) => c.slug === slug);
  if (!campaign || !isCampaignActive(campaign)) return undefined;
  return campaign;
}

export function getCampaignLandingPath(slug: string): string {
  return `/kampanya/${slug}`;
}
